import { createContext, useContext, useMemo, useState } from 'react'
import { useApiListAssetThumbnail, useApiListNodeCourse } from '../../../../../api-new/state/content'
import { usePlatformList } from '../common/platform/use-platform-list'
import { useLoad } from '../../../../../shared/hooks/load'
import { useFirstLoadSettled } from '../../../../../shared/empty-state/empty-state'
import { DATATYPE_CATEGORY, DATATYPE_COURSE, DATATYPE_STREAMING_VIDEO } from '../common/shared/metadata'

const DataContext = createContext()

export const useDataContext = () => useContext(DataContext)

export const DataProvider = ({ children }) => {
  const apiListNodeCourse = useApiListNodeCourse()
  const apiListAssetThumbnail = useApiListAssetThumbnail()

  useLoad(() => {
    apiListNodeCourse.get()
    apiListAssetThumbnail.get('content')
  }, [])

  // True once the first fetch has answered, so the page can tell "still
  // loading" from "nothing here" (and reloads never flash a loading state).
  const loaded = useFirstLoadSettled(apiListNodeCourse)

  const categories = useMemo(() => apiListNodeCourse.items.filter(node => node.datatype === DATATYPE_CATEGORY), [apiListNodeCourse])
  const courses = useMemo(() => apiListNodeCourse.items.filter(node => node.datatype === DATATYPE_COURSE), [apiListNodeCourse])
  const streamingVideos = useMemo(() => apiListNodeCourse.items.filter(node => node.datatype === DATATYPE_STREAMING_VIDEO), [apiListNodeCourse])

  // "Available on": the area's list for one platform, as content answers it
  // (the courses available there and the folders above them). The unfiltered
  // lists above stay as they are for everything else (titles, edit pages).
  const platformList = usePlatformList(useApiListNodeCourse)
  const { settled: filteredSettled, items: filteredItems, error: filteredError, reload: reloadFiltered } = platformList

  const filtered = useMemo(
    () => ({
      loaded: filteredSettled,
      error: filteredError,
      retry: reloadFiltered,
      items: filteredItems,
      categories: filteredItems.filter(node => node.datatype === DATATYPE_CATEGORY),
      courses: filteredItems.filter(node => node.datatype === DATATYPE_COURSE),
      streamingVideos: []
    }),
    [filteredSettled, filteredItems, filteredError, reloadFiltered]
  )

  // "Format" (owner 2026-10-09: "v2:", "plus:", ... in the search box): read
  // from each course's metadata in the browser, so it narrows whichever list
  // is shown. Like the platform, it belongs to the area.
  // The page shows and applies it to super admins only (useFormatFilter,
  // format-controls.js).
  const [format, setFormat] = useState(null)

  return (
    <DataContext.Provider
      value={{
        reload: async () => {
          await apiListNodeCourse.get()
          apiListAssetThumbnail.get('content')
          if (platformList.platform) reloadFiltered()
        },

        platform: platformList.platform,
        setPlatform: platformList.setPlatform,
        filtered: filtered,

        format: format,
        setFormat: setFormat,

        loaded: loaded,
        error: apiListNodeCourse.error,
        retry: () => apiListNodeCourse.get(),
        items: apiListNodeCourse.items,
        categories: categories,
        courses: courses,
        streamingVideos: streamingVideos,

        thumbnails: apiListAssetThumbnail.items
      }}>
      {children}
    </DataContext.Provider>
  )
}
